import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Alert,
  useTheme,
  IconButton,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Snackbar
} from '@mui/material';
import { ChromePicker } from 'react-color';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
  SmartToy as SmartToyIcon,
  Psychology as PsychologyIcon,
  Settings as SettingsIcon,
  FormatColorFill as ColorIcon,
  ChatBubble as ChatBubbleIcon,
  AutoAwesome as AutoAwesomeIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Article as ArticleIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const personalities = [
  { value: 'friendly', label: 'Friendly and Helpful', description: 'Warm, approachable and eager to help' },
  { value: 'professional', label: 'Professional', description: 'Formal, concise and business-oriented' },
  { value: 'casual', label: 'Casual', description: 'Relaxed, conversational and informal' },
  { value: 'enthusiastic', label: 'Enthusiastic', description: 'Energetic, positive and encouraging' },
  { value: 'technical', label: 'Technical Expert', description: 'Detail-oriented, precise and knowledgeable' }
];

const models = [
  { value: 'gpt-4o-mini', label: 'GPT-4o Mini', description: 'Fast, efficient with good quality responses' },
  { value: 'gpt-4o-mini-2024-07-18', label: 'GPT-4o Mini (July 2024)', description: 'Updated version with recent data' },
  { value: 'gpt-4.1-nano', label: 'GPT-4.1 Nano', description: 'Lightweight model, best for simple tasks' },
  { value: 'gpt-4.1-nano-2025-04-14', label: 'GPT-4.1 Nano (April 2025)', description: 'Latest nano model with improved reasoning' }
];

const CreateBot = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [aiPromptOpen, setAiPromptOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [fileUploading, setFileUploading] = useState(false);
  const [fileUploadError, setFileUploadError] = useState('');
  // Add missing snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    personality: 'friendly',
    preferredModel: 'gpt-4o-mini',
    customizations: {
      primaryColor: '#4C85F0',
      fontFamily: 'Roboto, sans-serif',
      position: 'bottom-right',
      welcomeMessage: 'Hello! How can I help you today?'
    }
  });
  
  const [validationErrors, setValidationErrors] = useState({
    name: '',
    description: '',
    welcomeMessage: ''
  });
  
  const steps = [
    'Basic Information',
    'Personality & Model',
    'Knowledge Files',  // New step for file upload
    'Customization'
  ];
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Clear validation errors when field is updated
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const generateAIDescription = async (prompt) => {
    setGeneratingDescription(true);
    setError("");
    
    try {
      // Use the name as part of the prompt if available
      const botName = formData.name.trim() ? formData.name : "the bot";
      const fullPrompt = prompt || 
        `Create a compelling and concise description for an AI chatbot named "${botName}". The description should explain what the bot does and how it can help users. Keep it between 50-100 words.`;

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/ai/generate-text`,
        { 
          prompt: fullPrompt,
          model: 'gpt-4o-mini',
          maxTokens: 100
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data && response.data.text) {
        setFormData(prev => ({
          ...prev,
          description: response.data.text
        }));
      } else {
        setError("Failed to generate description. Please try again.");
      }
    } catch (err) {
      console.error("Error generating AI description:", err);
      setError(err.response?.data?.message || "Failed to generate AI description. Please try again.");
    } finally {
      setGeneratingDescription(false);
      setAiPromptOpen(false);
      setAiPrompt("");
    }
  };

  const handleOpenAiPrompt = () => {
    setAiPromptOpen(true);
  };

  const handleCloseAiPrompt = () => {
    setAiPromptOpen(false);
  };

  const handleAiPromptChange = (e) => {
    setAiPrompt(e.target.value);
  };

  const handleAiPromptSubmit = () => {
    if (aiPrompt.trim()) {
      generateAIDescription(aiPrompt);
    } else {
      generateAIDescription();
    }
  };
  
  const handleColorChange = (color) => {
    setFormData(prev => ({
      ...prev,
      customizations: {
        ...prev.customizations,
        primaryColor: color.hex
      }
    }));
  };
  
  const validateCurrentStep = () => {
    let isValid = true;
    const newErrors = { ...validationErrors };
    
    if (activeStep === 0) {
      if (!formData.name.trim()) {
        newErrors.name = 'Bot name is required';
        isValid = false;
      } else if (formData.name.length < 3) {
        newErrors.name = 'Bot name must be at least 3 characters';
        isValid = false;
      }
      
      if (!formData.description.trim()) {
        newErrors.description = 'Description is required';
        isValid = false;
      } else if (formData.description.length < 10) {
        newErrors.description = 'Description must be at least 10 characters';
        isValid = false;
      }
    } else if (activeStep === 3) {
      if (!formData.customizations.welcomeMessage.trim()) {
        newErrors.welcomeMessage = 'Welcome message is required';
        isValid = false;
      }
    }
    
    setValidationErrors(newErrors);
    return isValid;
  };
  
  const handleNext = () => {
    if (validateCurrentStep()) {
      setActiveStep(prevStep => prevStep + 1);
    }
  };
  
  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };
  
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Check file size (max 20MB)
    if (file.size > 20 * 1024 * 1024) {
      setFileUploadError('File size must be less than 20MB');
      return;
    }
    
    // Check file type
    const allowedTypes = [
      'application/pdf', 
      'text/plain', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/csv',
      'application/json'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      setFileUploadError('File type not supported. Please upload PDF, TXT, DOCX, CSV, or JSON files');
      return;
    }
    
    setFileUploading(true);
    setFileUploadError('');
    
    try {
      // Create form data
      const formData = new FormData();
      formData.append('file', file);
      
      // Upload file to the server
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/file-upload/openai-assistant`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
        }
      );
      
      // Add the file to the list of uploaded files
      setUploadedFiles(prev => [...prev, {
        name: response.data.fileName,
        type: response.data.fileType,
        size: response.data.fileSize,
        openaiFileId: response.data.openaiFileId
      }]);
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'File uploaded successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      setFileUploadError(error.response?.data?.message || 'Failed to upload file. Please try again.');
    } finally {
      setFileUploading(false);
    }
  };
  
  const handleRemoveFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };
  
  const handleSubmit = async () => {
    if (!validateCurrentStep()) return;
    
    setSubmitting(true);
    setError("");
    
    try {
      // Include the OpenAI file IDs in the request
      const openaiFileIds = uploadedFiles.map(file => file.openaiFileId);
      
      // Create the bot with assistantId
      const botResponse = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bots`,
        {
          ...formData,
          openaiFileIds
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Redirect to the bot details page
      navigate(`/bots/${botResponse.data.id}`);
    } catch (err) {
      console.error("Error creating bot:", err);
      setError(err.response?.data?.message || "Failed to create bot. Please try again.");
      setSubmitting(false);
    }
  };
  
  // Add snackbar close handler
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };
  
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Let's start with the basics
            </Typography>
            
            <TextField
              name="name"
              label="Bot Name"
              fullWidth
              margin="normal"
              value={formData.name}
              onChange={handleChange}
              error={!!validationErrors.name}
              helperText={validationErrors.name || "Choose a name for your chatbot"}
              required
            />
            
            <TextField
              name="description"
              label="Description"
              fullWidth
              multiline
              rows={4}
              margin="normal"
              value={formData.description}
              onChange={handleChange}
              error={!!validationErrors.description}
              helperText={validationErrors.description || "Describe what your bot does and how it can help users"}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Generate AI description">
                      <IconButton
                        onClick={handleOpenAiPrompt}
                        edge="end"
                        disabled={generatingDescription}
                        sx={{
                          color: theme.palette.secondary.main,
                          animation: generatingDescription ? 'glow 1.5s infinite alternate' : 'none'
                        }}
                      >
                        {generatingDescription ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />

            <Dialog open={aiPromptOpen} onClose={handleCloseAiPrompt}>
              <DialogTitle>AI Description Generator</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Provide specific details about what you want your bot to do, or click "Generate" to create a generic description based on the bot's name.
                </DialogContentText>
                <TextField
                  autoFocus
                  margin="dense"
                  label="Prompt (optional)"
                  placeholder="E.g., Create a description for a customer service bot that helps with product inquiries and returns"
                  fullWidth
                  variant="outlined"
                  value={aiPrompt}
                  onChange={handleAiPromptChange}
                  multiline
                  rows={3}
                  sx={{ mt: 2 }}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseAiPrompt} color="primary">
                  Cancel
                </Button>
                <Button 
                  onClick={handleAiPromptSubmit} 
                  color="secondary" 
                  variant="contained"
                  disabled={generatingDescription}
                  startIcon={generatingDescription ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                >
                  {generatingDescription ? 'Generating...' : 'Generate'}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        );
      
      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Personality and AI Model
            </Typography>
            
            <FormControl fullWidth margin="normal">
              <InputLabel id="personality-label">Bot Personality</InputLabel>
              <Select
                labelId="personality-label"
                name="personality"
                value={formData.personality}
                onChange={handleChange}
                label="Bot Personality"
              >
                {personalities.map((personality) => (
                  <MenuItem key={personality.value} value={personality.value}>
                    {personality.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {personalities.find(p => p.value === formData.personality)?.description || "Select a personality that matches your brand"}
              </FormHelperText>
            </FormControl>
            
            <Box sx={{ my: 3 }}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="model-label">AI Model</InputLabel>
                <Select
                  labelId="model-label"
                  name="preferredModel"
                  value={formData.preferredModel}
                  onChange={handleChange}
                  label="AI Model"
                >
                  {models.map((model) => (
                    <MenuItem key={model.value} value={model.value}>
                      {model.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {models.find(m => m.value === formData.preferredModel)?.description || "Choose the AI model powering your bot"}
                </FormHelperText>
              </FormControl>
            </Box>
          </Box>
        );
        
      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Add Knowledge Files
            </Typography>
            
            <Typography variant="body2" color="text.secondary" paragraph>
              Upload files like PDFs, documents, or text files to enhance your bot's knowledge base.
              These files will be processed and used to improve your bot's responses.
            </Typography>
            
            <Box sx={{ mt: 3, mb: 2 }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
                fullWidth
                disabled={fileUploading}
                sx={{ py: 1.5 }}
              >
                {fileUploading ? 'Uploading...' : 'Select File to Upload'}
                <input
                  type="file"
                  hidden
                  onChange={handleFileUpload}
                  accept=".pdf,.txt,.docx,.csv,.json"
                  disabled={fileUploading}
                />
              </Button>
              
              {fileUploadError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {fileUploadError}
                </Alert>
              )}
            </Box>
            
            {fileUploading && (
              <Box sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Uploading file to OpenAI...
                </Typography>
                <LinearProgress />
              </Box>
            )}
            
            {uploadedFiles.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Uploaded Files ({uploadedFiles.length})
                </Typography>
                <Paper variant="outlined">
                  <List>
                    {uploadedFiles.map((file, index) => (
                      <ListItem
                        key={index}
                        secondaryAction={
                          <IconButton 
                            edge="end" 
                            aria-label="delete"
                            onClick={() => handleRemoveFile(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        }
                      >
                        <ListItemAvatar>
                          <Avatar>
                            <ArticleIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={file.name}
                          secondary={`${file.type.split('/')[1].toUpperCase()} • ${(file.size / 1024 / 1024).toFixed(2)} MB`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Box>
            )}
            
            {uploadedFiles.length === 0 && !fileUploading && (
              <Box 
                sx={{ 
                  mt: 4, 
                  p: 4, 
                  borderRadius: 2, 
                  bgcolor: 'rgba(0,0,0,0.02)', 
                  textAlign: 'center'
                }}
              >
                <ArticleIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  No files uploaded yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Files are optional but recommended for better bot responses
                </Typography>
              </Box>
            )}
          </Box>
        );
      
      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Customize Your Bot
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="position-label">Chat Widget Position</InputLabel>
                  <Select
                    labelId="position-label"
                    name="customizations.position"
                    value={formData.customizations.position}
                    onChange={handleChange}
                    label="Chat Widget Position"
                  >
                    <MenuItem value="bottom-right">Bottom Right</MenuItem>
                    <MenuItem value="bottom-left">Bottom Left</MenuItem>
                    <MenuItem value="top-right">Top Right</MenuItem>
                    <MenuItem value="top-left">Top Left</MenuItem>
                  </Select>
                  <FormHelperText>
                    Position of the chat widget on your website
                  </FormHelperText>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="font-label">Font Family</InputLabel>
                  <Select
                    labelId="font-label"
                    name="customizations.fontFamily"
                    value={formData.customizations.fontFamily}
                    onChange={handleChange}
                    label="Font Family"
                  >
                    <MenuItem value="Roboto, sans-serif">Roboto</MenuItem>
                    <MenuItem value="'Open Sans', sans-serif">Open Sans</MenuItem>
                    <MenuItem value="'Montserrat', sans-serif">Montserrat</MenuItem>
                    <MenuItem value="'Lato', sans-serif">Lato</MenuItem>
                    <MenuItem value="system-ui, sans-serif">System Default</MenuItem>
                  </Select>
                  <FormHelperText>
                    Font used in the chat interface
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 3, mb: 4 }}>
              <TextField
                name="customizations.welcomeMessage"
                label="Welcome Message"
                fullWidth
                margin="normal"
                value={formData.customizations.welcomeMessage}
                onChange={handleChange}
                error={!!validationErrors.welcomeMessage}
                helperText={validationErrors.welcomeMessage || "First message users will see when they open the chat"}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ChatBubbleIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            
            <Box sx={{ mt: 3, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Primary Color
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  onClick={() => setColorPickerOpen(!colorPickerOpen)}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    backgroundColor: formData.customizations.primaryColor,
                    cursor: 'pointer',
                    border: '2px solid #e0e0e0',
                    mr: 2
                  }}
                />
                
                <Typography>
                  {formData.customizations.primaryColor.toUpperCase()}
                </Typography>
                
                <IconButton 
                  size="small" 
                  onClick={() => setColorPickerOpen(!colorPickerOpen)}
                  sx={{ ml: 2 }}
                >
                  <ColorIcon />
                </IconButton>
              </Box>
              
              {colorPickerOpen && (
                <Box sx={{ mt: 2, position: 'relative', zIndex: 2 }}>
                  <Box 
                    sx={{ 
                      position: 'fixed',
                      top: 0,
                      right: 0,
                      bottom: 0,
                      left: 0
                    }} 
                    onClick={() => setColorPickerOpen(false)} 
                  />
                  <ChromePicker
                    color={formData.customizations.primaryColor}
                    onChange={handleColorChange}
                    disableAlpha
                  />
                </Box>
              )}
            </Box>
          </Box>
        );
      
      default:
        return 'Unknown step';
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      display: 'flex',
      background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, #2c3e50 100%)`,
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Animated background elements */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          zIndex: 0
        }}
      >
        {[...Array(6)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              width: ['100px', '150px', '200px'][i % 3],
              height: ['100px', '150px', '200px'][i % 3],
              background: `rgba(76, 133, 240, ${0.03 + (i % 3) * 0.02})`,
              borderRadius: '50%',
              top: `${10 + i * 15}%`,
              left: `${5 + i * 15}%`,
              animation: `float ${5 + i * 2}s infinite ease-in-out alternate`
            }}
          />
        ))}
      </Box>
      
      <Container component="main" maxWidth="md" sx={{ zIndex: 1, py: 8 }}>
        <Paper 
          sx={{ 
            p: 4, 
            borderRadius: 3,
            backdropFilter: 'blur(10px)',
            background: 'rgba(255, 255, 255, 0.9)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}
        >
          <Box display="flex" alignItems="center" mb={3}>
            <SmartToyIcon 
              sx={{ 
                color: theme.palette.primary.main, 
                fontSize: '2rem',
                mr: 1 
              }} 
            />
            <Typography variant="h4" component="h1" fontWeight="700">
              Create New Bot
            </Typography>
          </Box>
          
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel
                  StepIconProps={{
                    icon: index === 0 ? <SmartToyIcon /> : 
                           index === 1 ? <PsychologyIcon /> : 
                           index === 2 ? <ArticleIcon /> :
                           <SettingsIcon />
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
          
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          {getStepContent(activeStep)}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
              variant="outlined"
              sx={{ display: activeStep === 0 ? 'none' : 'flex' }}
            >
              Back
            </Button>
            
            <Button
              variant="contained"
              color="primary"
              onClick={activeStep === steps.length - 1 ? handleSubmit : handleNext}
              endIcon={activeStep === steps.length - 1 ? <CheckIcon /> : <ArrowForwardIcon />}
              disabled={submitting}
              sx={{
                py: 1.2,
                px: 3,
                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              }}
            >
              {submitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                activeStep === steps.length - 1 ? 'Create Bot' : 'Next'
              )}
            </Button>
          </Box>
        </Paper>
      </Container>
      
      {/* Add Snackbar component at the end of return statement */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      {/* CSS for animations */}
      <style jsx global>{`
        @keyframes float {
          0% {
            transform: translateY(0) rotate(0deg);
          }
          100% {
            transform: translateY(-20px) rotate(10deg);
          }
        }
        
        @keyframes glow {
          0% {
            filter: brightness(1);
          }
          100% {
            filter: brightness(1.5);
          }
        }
      `}</style>
    </Box>
  );
};

export default CreateBot;
